import React, { useEffect, useState } from 'react'

import { observer } from 'mobx-react'
import { FieldErrors, useForm } from 'react-hook-form'

import cn from 'classnames'
import emojiRegex from 'emoji-regex'
import moment from 'moment'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'

import { checkBadWords } from 'utils'
import { email, name } from 'utils/regExp'

import { toast } from 'App'
import Button from 'components/Button'
import ButtonIcon from 'components/ButtonIcon'
import UserInfoInput from 'components/UserInfoInput'

import { ISelected } from 'models'

import { useStore } from 'store'

import styles from './styles.module.scss'

interface IProps {
  currentUser?: boolean
}

const sexList: ISelected[] = [
  { value: 0, label: 'Male' },
  { value: 1, label: 'Female' },
  { value: 2, label: 'Another' },
]

const SignUpSchema = yup.object().shape(
  {
    FirstName: yup
      .string()
      .notRequired()
      .when('FirstName', {
        is: (value: string) => value?.length,
        then: value =>
          value.min(2, 'First name should be longer than 2 letters'),
      })
      .when('FirstName', {
        is: (value: string) => value?.length,
        then: value =>
          value.test(
            'badWords',
            'You are not allowed to use prohibited words',
            (value, context) => {
              return !checkBadWords(value)
            }
          ),
      })
      .when('FirstName', {
        is: (value: string) => value?.length,
        then: value =>
          value.test(
            'symbols',
            'You are not allowed to use emojis, numbers, and smiles',
            (value, context) => Boolean(name(value))
          ),
      })
      .when('FirstName', {
        is: (value: string) => value?.length,
        then: value =>
          value.test(
            'emoji',
            'You are not allowed to use emojis or smiles',
            (value, context) => !emojiRegex().test(value as string)
          ),
      }),
    LastName: yup
      .string()
      .notRequired()
      .when('LastName', {
        is: (value: string) => value?.length,
        then: value =>
          value.min(2, 'Last name should be longer than 2 letters'),
      })
      .when('LastName', {
        is: (value: string) => value?.length,
        then: value =>
          value.test(
            'badWords',
            'You are not allowed to use prohibited words',
            (value, context) => {
              return !checkBadWords(value)
            }
          ),
      })
      .when('LastName', {
        is: (value: string) => value?.length,
        then: value =>
          value.test(
            'symbols',
            'You are not allowed to use emojis, numbers, and smiles',
            (value, context) => Boolean(name(value))
          ),
      })
      .when('LastName', {
        is: (value: string) => value?.length,
        then: value =>
          value.test(
            'emoji',
            'You are not allowed to use emojis or smiles',
            (value, context) => !emojiRegex().test(value as string)
          ),
      }),
    Hometown: yup.string(),
  },
  [
    ['FirstName', 'FirstName'],
    ['LastName', 'LastName'],
  ]
)

//Resize
const UserInfoBasic: React.FC<IProps> = observer(({ currentUser }) => {
  const { profile } = useStore()
  const [change, setChange] = useState(false)
  const {
    UserGender,
    DateOfBirth,
    Hometown,
    FirstName,
    LastName,
    DeletionMark,
  } = profile.viewedUser

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: yupResolver(SignUpSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  useEffect(() => {
    if (!currentUser) setChange(false)
  }, [currentUser])

  const onSubmit = async (data: Record<string, string>) => {
    await profile.onChangeUserInfo(data)
    setChange(false)
  }

  const onError = (err: FieldErrors) => {
    // if (err.FirstName?.message) {
    //   toast({
    //     type: 'warning',
    //     message: err.FirstName.message
    //   })
    // }
    // if (err.LastName?.message) {
    //   toast({
    //     type: 'warning',
    //     message: err.LastName.message
    //   })
    // }
  }

  return (
    <form
      className={cn({
        [styles.cartBody]: true,
        [styles.cartBodyCurrent]: currentUser,
        [styles.cartBody_deletedUser]: DeletionMark,
      })}
      onSubmit={handleSubmit(onSubmit as any, onError)}
    >
      <div className={styles.titleBlock}>
        <div className={styles.title}>Basic Info</div>
        {currentUser && (
          <ButtonIcon
            style={{ pointerEvents: change ? 'none' : 'auto' }}
            icon={'edit'}
            onClick={() => setChange(true)}
          />
        )}
      </div>
      {UserGender !== null || change ? (
        <UserInfoInput
          placeholder={'Sex'}
          type={'select'}
          title={'Sex'}
          edit={change}
          value={UserGender}
          name={'UserGender'}
          register={register}
          optionsList={sexList}
        />
      ) : null}

      {FirstName || change ? (
        <UserInfoInput
          placeholder={'FirstName'}
          title={'First Name'}
          edit={change}
          value={!DeletionMark ? FirstName : 'Deleted'}
          name={'FirstName'}
          register={register}
          clearErrors={clearErrors}
          error={errors.FirstName?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
        />
      ) : null}

      {LastName || change ? (
        <UserInfoInput
          placeholder={'LastName'}
          title={'Last Name'}
          edit={change}
          value={!DeletionMark ? LastName : 'User'}
          name={'LastName'}
          register={register}
          clearErrors={clearErrors}
          error={errors.LastName?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
        />
      ) : null}

      {/*<Input*/}
      {/*  title={'Birthday'}*/}
      {/*  edit={false}*/}
      {/*  value={moment(DateOfBirth).format('MMMM DD, YYYY')}*/}
      {/*  name={'DateOfBirth'}*/}
      {/*/>*/}

      {(Hometown || change) && !DeletionMark ? (
        <UserInfoInput
          placeholder={'Hometown'}
          title={'Hometown'}
          edit={change}
          value={Hometown}
          name={'Hometown'}
          register={register}
          error={errors.Hometown?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
        />
      ) : null}
      {change ? (
        <div className={styles.saveBlock}>
          <div className={styles.cancelButton} onClick={() => setChange(false)}>
            Cancel
          </div>
          <Button
            type={'submit'}
            style={{
              background: 'linear-gradient(#2E2B63, #2E2B63)',
              boxShadow: '0px 7px 25px rgba(0, 0, 0, 0.0563538)',
              borderRadius: 11,
              width: '92px',
              height: '40px',
            }}
            styleText={{ color: '#ffffff' }}
            btnStyle="classic"
            text="Save"
          />
        </div>
      ) : null}
    </form>
  )
})

export default UserInfoBasic
